import { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  CheckCircle,
  Storage,
  Description,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import { UploadedFile } from '../../types';
import { getUserAccessToken } from '../../utils/tokenStorage';

interface MultiFileUploadProps {
  requiredCount: number;
  onUploadComplete: (files: UploadedFile[]) => void;
  onUploadProgress?: (progress: number) => void;
  actionId: number;
  projectId: number;
  folderPath: string;
  siteId: string;
  recipientEmail?: string;
}

interface FileWithProgress {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
  uploadedFile?: UploadedFile;
}

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

export default function MultiFileUpload({
  requiredCount,
  onUploadComplete,
  onUploadProgress,
  actionId,
  projectId,
  folderPath,
  siteId,
  recipientEmail,
}: MultiFileUploadProps) {
  const [files, setFiles] = useState<FileWithProgress[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  // Define handleFiles first using useCallback so it can be referenced by other handlers
  const handleFiles = useCallback((newFiles: File[]) => {
    setError(null);

    // Validate file count
    setFiles(prev => {
      const totalFiles = prev.length + newFiles.length;
      if (totalFiles > requiredCount) {
        setError(`Cannot add more than ${requiredCount} file(s). You have ${prev.length} file(s) selected and tried to add ${newFiles.length} more.`);
        return prev;
      }

      // Validate file sizes
      const oversizedFiles = newFiles.filter(f => f.size > MAX_FILE_SIZE);
      if (oversizedFiles.length > 0) {
        setError(`Some files exceed the maximum size of ${MAX_FILE_SIZE / 1024 / 1024}MB: ${oversizedFiles.map(f => f.name).join(', ')}`);
        return prev;
      }

      // Add new files
      const filesWithProgress: FileWithProgress[] = newFiles.map(file => ({
        file,
        progress: 0,
        status: 'pending' as const,
      }));

      return [...prev, ...filesWithProgress];
    });
  }, [requiredCount]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  }, [handleFiles]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    handleFiles(selectedFiles);
  }, [handleFiles]);

  const handleRemoveFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setError(null);
  };

  const handleUpload = async () => {
    // Validate file count
    if (files.length !== requiredCount) {
      setError(`Please select exactly ${requiredCount} file(s). Currently selected: ${files.length}`);
      return;
    }

    setError(null);
    setUploading(true);

    try {
      const formData = new FormData();
      
      // Append all files
      files.forEach(fileWithProgress => {
        formData.append('files', fileWithProgress.file);
      });

      // Append required fields
      formData.append('projectId', projectId.toString());
      formData.append('siteId', siteId);
      // libraryName removed - backend auto-detects from airport
      formData.append('folderPath', folderPath);
      formData.append('actionId', actionId.toString());

      if (recipientEmail) {
        formData.append('recipientEmail', recipientEmail);
      }

      const userAccessToken = getUserAccessToken();
      if (userAccessToken) {
        formData.append('userAccessToken', userAccessToken);
      }

      // Update files to uploading status
      setFiles(prev => prev.map(f => ({ ...f, status: 'uploading' as const, progress: 0 })));

      // Upload with progress tracking
      const result = await apiService.createFiles(formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          
          // Distribute progress evenly across all files
          const progressPerFile = percentCompleted / files.length;
          setFiles(prev => prev.map(f => ({
            ...f,
            progress: Math.min(progressPerFile, 100),
          })));

          if (onUploadProgress) {
            onUploadProgress(percentCompleted);
          }
        },
      });

      // Update files with uploaded file info
      const uploadedFiles: UploadedFile[] = result.uploadedFiles || [];
      setFiles(prev => prev.map((f, index) => {
        const uploaded = uploadedFiles[index];
        return {
          ...f,
          status: uploaded ? ('completed' as const) : ('error' as const),
          progress: uploaded ? 100 : 0,
          uploadedFile: uploaded,
          error: uploaded ? undefined : 'Upload failed',
        };
      }));

      // Check if all files uploaded successfully
      const allCompleted = uploadedFiles.length === files.length;
      if (allCompleted) {
        onUploadComplete(uploadedFiles);
      } else {
        setError('Some files failed to upload. Please try again.');
      }
    } catch (err: unknown) {
      console.error('Upload error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload files. Please try again.';
      setError(errorMessage);
      
      // Mark all files as error
      const errorMsg = err instanceof Error ? err.message : 'Upload failed';
      setFiles(prev => prev.map(f => ({
        ...f,
        status: 'error' as const,
        error: errorMsg,
      })));
    } finally {
      setUploading(false);
    }
  };

  const canUpload = files.length === requiredCount && !uploading;
  const uploadedCount = files.filter(f => f.status === 'completed').length;

  return (
    <Box>
      <Paper
        sx={{
          p: 3,
          border: dragActive ? '2px dashed' : '2px dashed',
          borderColor: dragActive ? 'primary.main' : 'grey.300',
          bgcolor: dragActive ? 'action.hover' : 'background.paper',
          transition: 'all 0.2s',
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            Upload {requiredCount} Document{requiredCount !== 1 ? 's' : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Drag and drop files here, or click to select
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Maximum file size: {MAX_FILE_SIZE / 1024 / 1024}MB per file
          </Typography>

          <input
            type="file"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            id="multi-file-upload-input"
            disabled={uploading || files.length >= requiredCount}
          />
          <label htmlFor="multi-file-upload-input">
            <Button
              variant="outlined"
              component="span"
              disabled={uploading || files.length >= requiredCount}
              startIcon={<CloudUpload />}
            >
              Select Files
            </Button>
          </label>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {files.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Selected Files ({files.length} of {requiredCount})
            </Typography>

            {files.length !== requiredCount && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Please select exactly {requiredCount} file(s). Currently selected: {files.length}
              </Alert>
            )}

            <List dense>
              {files.map((fileWithProgress, index) => (
                <ListItem
                  key={index}
                  secondaryAction={
                    fileWithProgress.status !== 'uploading' && (
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveFile(index)}
                        disabled={uploading}
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    )
                  }
                >
                  <ListItemIcon>
                    {fileWithProgress.status === 'completed' ? (
                      <CheckCircle color="success" />
                    ) : fileWithProgress.status === 'error' ? (
                      <Description color="error" />
                    ) : (
                      <Description />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={fileWithProgress.file.name}
                    secondary={
                      <Box>
                        <Typography variant="caption" display="block">
                          {(fileWithProgress.file.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                        {fileWithProgress.status === 'uploading' && (
                          <LinearProgress
                            variant="determinate"
                            value={fileWithProgress.progress}
                            sx={{ mt: 1 }}
                          />
                        )}
                        {fileWithProgress.status === 'completed' && fileWithProgress.uploadedFile && (
                          <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip
                              icon={fileWithProgress.uploadedFile.storageType === 'SHAREPOINT' ? <CloudUpload /> : <Storage />}
                              label={fileWithProgress.uploadedFile.storageType}
                              size="small"
                              color="success"
                            />
                            <Typography variant="caption" color="text.secondary">
                              Document {fileWithProgress.uploadedFile.documentNumber}
                            </Typography>
                          </Box>
                        )}
                        {fileWithProgress.status === 'error' && (
                          <Typography variant="caption" color="error">
                            {fileWithProgress.error || 'Upload failed'}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {uploadedCount === requiredCount && uploadedCount > 0 && (
          <Alert severity="success" sx={{ mt: 2 }}>
            All {uploadedCount} file(s) uploaded successfully!
          </Alert>
        )}

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!canUpload}
            startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}
          >
            {uploading ? 'Uploading...' : `Upload ${files.length} File${files.length !== 1 ? 's' : ''}`}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
